/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import {
  Segment,
  Transition,
  Container,
  Dimmer,
  Loader,
  Grid,
  Button,
  Accordion,
  Popup,
} from "semantic-ui-react";
import { useLazyQuery } from "@apollo/react-hooks";
import {
  GetUsageQuery,
} from "../../../graphql/subscription";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import {
  authCheckCallApi,
  getPlanInfo,
  DateFormatLong,
  isMonitoringPlan,
} from "../../Authenticator/utils";
import { AuthProps } from "aws-amplify-react/props";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import {
  OrganizationQuery,
  OrganizationData
} from "../../../graphql/organization";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

const Payment: React.FC<AuthProps> = ({ authData }) => {

  const { t } = useTranslation(["billing", "common"]);

  const { plan: currentPlan } = authData.signInUserSession.idToken.payload;
  const { plan, orgId, planExpires } =
    authData.signInUserSession.idToken.payload;
  const [showUsage, setShowUsage] = useState(false);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);


  const [getOrg, { loading: loadingOrgInfo }] = useLazyQuery<OrganizationData>(
    OrganizationQuery,
    {
      fetchPolicy: "cache-and-network"
    }
  );

  const [getUsage, { data: usageData }] = useLazyQuery<any>(GetUsageQuery, {
    variables: { orgId: orgId },
  });

  useEffect(() => {
    authCheckCallApi(getOrg);

    if (currentPlan !== "free") {
      authCheckCallApi(getUsage);
    }
  }, [getOrg, getUsage, currentPlan]);

  const planInfo = getPlanInfo(plan);

  const isPlanExpires =
    planExpires && new Date(parseInt(planExpires)) > new Date();

  const usageContents = () => {
    const appsProtected = usageData.usage.appsProtected.length;
    const periodEndDate = new Date(usageData.usage.periodEnd);
    const now = new Date();

    const periodDaysLeft =
      (periodEndDate.getTime() - now.getTime()) / (1000 * 3600 * 24);
    const periodDaysLeftStr = periodDaysLeft.toFixed(0);

    const showProtectedApps =
      ["protectStarter", "protectStarterSingleApp", "protectStandard"].includes(
        currentPlan
      ) || isMonitoringPlan(currentPlan);

    const billingPeriodApps = () => {
      return (
        <div>
          {usageData.usage.appsProtected.map((app: any) => {
            const appData = app.appId.split(":");
            return (
              <div key={app.appId} style={{ lineHeight: "30px" }}>
                <Ico
                  name={appData[1] === "android" ? "android" : "ios"}
                  style={{
                    verticalAlign: "middle",
                    marginRight: "10px",
                  }}
                />
                {appData[0]}
              </div>
            );
          })}
          <div
            style={{ borderTop: "1px solid #d9d9d9", margin: "10px 0" }}
          ></div>
          <div style={{ textAlign: "right" }}>
            {`Successful builds: ${usageData.usage.totalProtections}`}
            {["protectStarter", "protectStarterSingleApp"].includes(currentPlan)
              ? ` (out of ${planInfo.maxVers} available)`
              : ""}
          </div>
        </div>
      );
    };

    return (
      <Accordion style={{ width: "100%" }}>
        <Accordion.Title
          className={styles.advancedTitle}
          active={showUsage === true}
          index={1}
          onClick={() => {
            setShowUsage(!showUsage);
          }}
        >
          <Ico
            name={showUsage === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          {t("billing_period")}
        </Accordion.Title>
        <Accordion.Content active={showUsage === true}>
          <Grid stackable>
            <Grid.Row style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <Grid.Column
                width={4}
                verticalAlign="middle"
                style={{ marginLeft: "2rem", color: "#555" }}
              >
                {t("current_period")}
              </Grid.Column>

              {showProtectedApps && (
                <Grid.Column width={4}>
                  {appsProtected ? (
                    <Popup
                      wide
                      basic
                      position="bottom left"
                      mouseLeaveDelay={300}
                      content={billingPeriodApps()}
                      trigger={
                        <span className={styles.myAccountLink}>
                          <span>
                            {`${appsProtected} ${
                              appsProtected > 1
                                ? t("common:apps")
                                : t("common:app")
                            } ${t("protected_successfully")}`}
                            <Ico
                              name="help"
                              style={{
                                marginLeft: "5px",
                                verticalAlign: "text-bottom",
                              }}
                              className={styles.ico}
                            />
                          </span>
                        </span>
                      }
                    />
                  ) : (
                    t("no_apps_protected")
                  )}
                </Grid.Column>
              )}

              <Grid.Column width={4}>
                <Popup
                  basic
                  wide
                  mouseLeaveDelay={300}
                  position="bottom left"
                  content={
                    <div>
                      {t("billing_ends")}{" "}
                      {periodEndDate.toLocaleDateString(
                        navigator.language,
                        DateFormatLong
                      )}
                    </div>
                  }
                  trigger={
                    <span className={styles.myAccountLink}>
                      <span>
                        {`${periodDaysLeftStr} ${
                          periodDaysLeftStr === "1" ? "day" : "days"
                        } remaining`}
                        <Ico
                          name="cal"
                          style={{
                            marginLeft: "5px",
                            verticalAlign: "text-bottom",
                          }}
                          className={styles.ico}
                        />
                      </span>
                    </span>
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>
      </Accordion>
    );
  };

  const dimmerActive = loadingOrgInfo;

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={dimmerActive}>
              <Loader active={dimmerActive} size="large">
                {t("common:loading")}
              </Loader>
            </Dimmer>

            <div className={styles.sectionHeaderMargin}>
              {t("subscription_details")}
            </div>

            <Grid stackable>
              <Grid.Row verticalAlign="middle">
                <Grid.Column
                  width={4}
                  verticalAlign="middle"
                  className={styles.myAccountNoUsersLabel}
                >
                  {t("current_sub")}
                </Grid.Column>

                <Grid.Column width={4} verticalAlign="middle">
                  <div
                    className={styles.myAccountInfoLabel}
                    style={{ color: planInfo.color }}
                  >
                    {planInfo.name}
                  </div>
                </Grid.Column>
              </Grid.Row>

              {isPlanExpires && (
                <Grid.Row>
                  <Grid.Column
                    width={4}
                    className={styles.myAccountNoUsersLabel}
                  >
                    {t("subscription_active")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <label className={styles.paymentMethodDetails}>
                      {new Date(parseInt(planExpires)).toLocaleDateString(
                        navigator.language
                      )}
                    </label>
                  </Grid.Column>
                </Grid.Row>
              )}

              {usageData && usageData.usage && (
                <Grid.Row style={{ paddingBottom: 0 }}>
                  {usageContents()}
                </Grid.Row>
              )}

              {isPlanExpires && (
                <Grid.Row>
                  <Grid.Column stretched>
                    <div className={styles.buttonContainer}>
                      <Button
                        style={{ marginLeft: "1rem" }}
                        primary
                        className={cStyles.buttonPrime}
                        onClick={() => {
                          setMessage({
                            title: t("resume_subscription"),
                            message: t("contact_resume"),
                          });
                        }}
                      >
                        <Ico name="check" style={{ marginRight: "10px" }} />
                        {t("resume_subscription")}
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </Segment>
        </Transition>
      </Container>

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Payment;
